// ═══ THE MOVE: the rules, in one place ═════════════════════════════════════
// (owner ask 2026-10-03: select one or many words, move them to another
// dictionary, or found one and move them into it in one step.)
//
// A DICTIONARY IS A TAG (owner call 2026-09-20; its node, `lexicon`, holds only
// its name and kind), so a word stands in as many dictionaries as it wears tags,
// and "every word" (the master room, MASTER = '') is the list with no lens on.
// A move therefore means, by where it starts:
//   · from a dictionary: the word LEAVES it and JOINS the target; every other
//     dictionary it stands in keeps it (moving is not a reset of its tags)
//   · from every word:   the word JOINS the target; nothing is left
//   · to every word:      the word LEAVES the dictionary it is moved from, and
//                          stays in the master room, as every word does
// A word already in the target is left as it is (from a dictionary, it still
// leaves that one). THE STANCE IS INHERITED, as on any tag edit: joining an
// anti-dictionary makes the word an anti-word; leaving its last one makes it a
// word again.
//
// Each word's change is a TAG OP — { id, add, remove, stance? } — applied to the
// tags the graph holds at write time (never a stale whole list), so the move and
// its undo cannot clobber a tag added elsewhere meanwhile. The undo is the
// inverse op, computed from what the graph held before the write.
//
// Shared by the server (dict.js, POST /api/words/retag) and the web client
// (web/src/store.js, web/src/moving.js); the Mac ports it in Move.swift.

export const MASTER = ''

/** the tags after an op: removals first, then additions in order, no repeats */
export function applyTagOp(tags, op) {
  const drop = new Set((op && op.remove) || [])
  const out = (Array.isArray(tags) ? tags : []).filter(t => !drop.has(t))
  for (const t of (op && op.add) || []) if (!out.includes(t)) out.push(t)
  return out
}

/** THE ANTI-WORD IS INHERITED (owner call 2026-10-01): the stance a tag change
    implies, or null when it leaves a hand-set stance alone */
export function inheritedStance(word, tags, isAnti) {
  const avoided = word && word.stance === 'avoided'
  const was = ((word && word.tags) || []).some(isAnti), is = (tags || []).some(isAnti)
  if (is && !avoided) return 'avoided'
  if (!is && was && avoided) return 'held'
  return null
}

/** the op that takes a word back to `before` ({ tags, stance }), undoing `op` */
export function inverseOp(before, op) {
  const had = new Set((before && before.tags) || [])
  const back = {
    id: op.id,
    add: (op.remove || []).filter(t => had.has(t)),
    remove: (op.add || []).filter(t => !had.has(t)),
  }
  if (op.stance && before && op.stance !== before.stance) back.stance = before.stance === 'avoided' ? 'avoided' : 'held'
  return back
}

export const isNoop = op => !(op.add && op.add.length) && !(op.remove && op.remove.length) && !op.stance

/** PLANNING A MOVE over the words as this client holds them.
    words: [{ id, tags, stance, owner?, local? }]; ids: the words to move;
    to / from: dictionary tags, MASTER for every word; isAnti(tag) → bool.
    Returns { ops, unchanged, skipped }: an op for every word that changes,
    the ids already where the move would put them, and the ids that cannot
    move, each with its reason. Throws on a move that means nothing. */
export function planMove(words, ids, { to, from = MASTER, isAnti = () => false, isLocal = id => String(id || '').startsWith('local_') } = {}) {
  to = typeof to === 'string' ? to.trim() : ''
  from = typeof from === 'string' ? from : MASTER
  if (to === from) throw new Error(to ? `the words are already in “${to}”` : 'the words are already in every word')
  const byId = new Map((words || []).map(w => [w.id, w]))
  const ops = [], unchanged = [], skipped = []
  for (const id of [...new Set(ids || [])]) {
    const w = byId.get(id)
    if (!w) { skipped.push({ id, reason: 'gone' }); continue }
    if (w.owner) { skipped.push({ id, reason: 'not yours' }); continue }
    if (isLocal(id)) { skipped.push({ id, reason: 'not saved yet' }); continue }
    const tags = w.tags || []
    const op = {
      id,
      add: to && !tags.includes(to) ? [to] : [],
      remove: from && tags.includes(from) ? [from] : [],
    }
    const stance = inheritedStance(w, applyTagOp(tags, op), isAnti)
    if (stance) op.stance = stance
    if (isNoop(op)) unchanged.push(id); else ops.push(op)
  }
  return { ops, unchanged, skipped }
}

/** THE THREE VERBS (owner call 2026-10-04: every app whose collections are tags
    offers them, as people.oS does), each a plan over the same rule:
      · MOVE        planMove(…, { to, from })    leave the dictionary in view, join the target
      · ADD TO      planAdd(…, { to })           join the target, keep every other dictionary
      · REMOVE FROM planRemove(…, { from })      leave the dictionary in view; the word stays in
                                                 every word (the master room) and its others
    The stance is inherited as on any tag edit: adding to an anti-dictionary makes an anti-word;
    removing from the last one makes a word again. Removing from every word means nothing. */
export function planAdd(words, ids, { to, isAnti, isLocal } = {}) {
  return planMove(words, ids, { to, from: MASTER, isAnti, ...(isLocal ? { isLocal } : {}) })
}
export function planRemove(words, ids, { from, isAnti, isLocal } = {}) {
  if (!from) throw new Error('every word keeps every word: there is no dictionary to remove them from')
  return planMove(words, ids, { to: MASTER, from, isAnti, ...(isLocal ? { isLocal } : {}) })
}

/** run fn over items, at most `limit` at a time; every item answers, in order:
    { ok: true, value } or { ok: false, error } — one failure never stops the rest */
export async function mapLimit(items, limit, fn) {
  const out = new Array(items.length)
  let next = 0
  const lane = async () => {
    while (next < items.length) {
      const i = next++
      try { out[i] = { ok: true, value: await fn(items[i], i) } }
      catch (error) { out[i] = { ok: false, error } }
    }
  }
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, lane))
  return out
}

/** a tag op off the wire, cleaned; null when it is not one */
export const ID = /^[A-Za-z0-9_-]{1,80}$/
export function cleanOp(raw) {
  if (!raw || typeof raw !== 'object' || !ID.test(String(raw.id || ''))) return null
  const list = v => (Array.isArray(v) ? v : []).map(t => String(t == null ? '' : t).trim()).filter(Boolean)
  const add = list(raw.add), remove = list(raw.remove)
  if (add.length > 20 || remove.length > 20 || [...add, ...remove].some(t => t.length > 120)) return null
  if (raw.stance != null && raw.stance !== 'held' && raw.stance !== 'avoided') return null
  return { id: String(raw.id), add, remove, ...(raw.stance ? { stance: raw.stance } : {}) }
}
